import { BidToken } from '../common/BidToken';
import { SEAT_NAMES } from '../table-session/SeatLabel';
import type {
  PredictedAuctionEntry,
  PredictedBidCorrectness,
  PredictedJeffBid,
} from '../../types/predictedJeffBids';
import type { Position } from '../../types/problems';

/**
 * The AI's predicted auction (feature 025-ai-predicted-bids).
 *
 * Laid out as the same four-column grid the real auction uses, so the two panels can be read
 * across against each other call by call. It is a separate component rather than a mode of the
 * shared `AuctionTable` because what it renders is a different kind of thing: predicted calls
 * carry reasoning, a verdict and a click target, and the real auction carries BBO alerts. Fusing
 * them would mean one component with two disjoint halves.
 *
 * Only JEFF's calls are interactive. The engine reasoned about partner and opponents purely as
 * context and recorded no justification for their calls, so those render as plain text — a
 * button that could only ever say "nothing to show here" is worse than no button.
 *
 * The verdict is never colour-only (WCAG 1.4.1): every mark is a glyph with a text alternative.
 */

const SEATS: Position[] = ['W', 'N', 'E', 'S'];

/**
 * What each verdict shows and says. Only `incorrect` is a failure: `not_comparable` and
 * `declined` are both "no comparison to make" for different reasons — see the doc comment — and
 * neither is drawn in the failure red, so a reviewer scanning the column sees the real
 * disagreements rather than a wall of ✗.
 */
const CORRECTNESS_MARK: Record<PredictedBidCorrectness, { glyph: string; label: string; className: string }> = {
  correct: {
    glyph: '✓',
    label: 'matched Jeff',
    className: 'text-[#1b6b3a]',
  },
  incorrect: {
    glyph: '✗',
    label: 'differs from Jeff',
    className: 'text-[#a11b1b]',
  },
  not_comparable: {
    glyph: '–',
    label: 'answers a different auction',
    className: 'text-ink-soft',
  },
  declined: {
    // A distinct glyph rather than the not-comparable dash: both mean "not a disagreement", but
    // they arise differently and a reviewer deciding whether to write a correction needs to tell
    // them apart. The screen-reader label carries the whole meaning, since the glyph alone
    // cannot (WCAG 1.4.1).
    glyph: '⊘',
    label: 'no rule — the AI did not predict this call',
    className: 'text-ink-soft',
  },
};

export interface PredictedAuctionTableProps {
  /** The predicted calls in bidding order, across all four seats. */
  auction: PredictedAuctionEntry[];
  /** Jeff's predicted calls, keyed by the ordinal the auction entries carry. */
  jeffBids: PredictedJeffBid[];
  /** Seat whose calls are Jeff's — the only clickable ones. */
  jeffSeat: Position;
  /** Currently selected Jeff bid ordinal, or null when nothing is selected. */
  selectedOrdinal: number | null;
  /** Invoked with the ordinal of the clicked Jeff bid. */
  onSelectBid: (ordinal: number) => void;
  /** True when the prediction reproduced Jeff's auction exactly — drives the summary badge. */
  isExactMatch: boolean;
  /**
   * True when the hand diverged only because the engine DECLINED to predict (a `(no rule)` call
   * at the divergence), not because it offered a call Jeff did not make. Badged differently:
   * saying "AI differed from Jeff" about a call it never made is simply untrue, and on the
   * current data that is the majority of divergence.
   */
  divergenceIsDeclined?: boolean;
  /**
   * Ordinal of Jeff's first divergent call. When it is past his last predicted call the engine
   * stopped before the real auction ended, and the note goes under the table instead of against
   * a bid, because there is no predicted bid there to mark.
   */
  firstDiffJeffCallNo?: number | null;
}

/** Splits the flat call list into rows of four, seat-aligned with the dealer's rotation. */
function buildRows(auction: PredictedAuctionEntry[], header: Position[]): (PredictedAuctionEntry | null)[][] {
  if (auction.length === 0) return [];

  const rows: (PredictedAuctionEntry | null)[][] = [];
  let current: (PredictedAuctionEntry | null)[] = new Array(4).fill(null);
  let filled = 0;

  for (const entry of auction) {
    const column = header.indexOf(entry.seat);
    // A seat the header does not know about would silently vanish; keep the call rather than
    // lose it, in its own row.
    if (column < 0) continue;
    if (filled > 0 && column === 0) {
      rows.push(current);
      current = new Array(4).fill(null);
      filled = 0;
    }
    current[column] = entry;
    filled++;
  }
  if (filled > 0) rows.push(current);
  return rows;
}

export function PredictedAuctionTable({
  auction,
  jeffBids,
  jeffSeat,
  selectedOrdinal,
  onSelectBid,
  isExactMatch,
  divergenceIsDeclined = false,
  firstDiffJeffCallNo,
}: PredictedAuctionTableProps) {
  const byOrdinal = new Map(jeffBids.map(b => [b.ordinal, b]));

  // Rotate so the first caller leads the table, matching how the real auction is drawn.
  const firstSeat = auction[0]?.seat;
  const start = firstSeat ? Math.max(0, SEATS.indexOf(firstSeat)) : 0;
  const header = [...SEATS.slice(start), ...SEATS.slice(0, start)];
  const rows = buildRows(auction, header);

  // "Past the last predicted call" is how the analysis records an engine that stopped while the
  // real auction carried on: there is no predicted bid to mark, so the fact is stated in words.
  const stoppedEarly =
    typeof firstDiffJeffCallNo === 'number' && firstDiffJeffCallNo > jeffBids.length;

  return (
    <div data-testid="predicted-auction">
      {/* Three outcomes, not two. "Declined" sits between matched and differed: the engine
          reached no call here, so neither the green claim nor the red one is true of it. It is
          drawn neutral so the red badge keeps meaning "a real disagreement to review". */}
      <p
        className={
          'mb-2.5 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-bold ' +
          (isExactMatch
            ? 'bg-[#dff0e3] text-[#14532d]'
            : divergenceIsDeclined
              ? 'bg-[#ece7db] text-[#4a4336]'
              : 'bg-[#f6e0e0] text-[#7f1d1d]')
        }
        data-testid="prediction-verdict"
        data-verdict={isExactMatch ? 'matched' : divergenceIsDeclined ? 'declined' : 'differed'}
      >
        <span aria-hidden="true">
          {isExactMatch ? '✓' : divergenceIsDeclined ? '⊘' : '✗'}
        </span>
        {isExactMatch
          ? 'AI matched Jeff'
          : divergenceIsDeclined
            ? 'AI had no rule for this auction'
            : 'AI differed from Jeff'}
      </p>

      {auction.length === 0 ? (
        <p className="text-[14px] text-ink-soft" data-testid="predicted-auction-empty">
          No predicted auction was recorded for this hand.
        </p>
      ) : (
        <table className="w-full table-fixed border-collapse text-[14px]">
          <caption className="sr-only">
            The auction predicted by the AI. Jeff&apos;s own calls can be selected to read why the
            AI chose them.
          </caption>
          <thead>
            <tr>
              {header.map(seat => (
                <th
                  key={seat}
                  scope="col"
                  className="border-b border-line-strong pb-1 text-center text-[11px] font-semibold uppercase tracking-wider text-ink-soft"
                >
                  <span aria-hidden="true">{seat}</span>
                  <span className="sr-only">{SEAT_NAMES[seat]}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((entry, columnIndex) => (
                  <td key={columnIndex} className="py-0.5 text-center align-middle">
                    {entry === null ? (
                      <span className="text-ink-soft" aria-hidden="true">
                        –
                      </span>
                    ) : entry.jeffBidOrdinal != null ? (
                      <JeffCall
                        entry={entry}
                        bid={byOrdinal.get(entry.jeffBidOrdinal)}
                        isSelected={selectedOrdinal === entry.jeffBidOrdinal}
                        onSelect={() => onSelectBid(entry.jeffBidOrdinal as number)}
                      />
                    ) : (
                      <span className="text-ink-soft">
                        <BidToken bid={entry.bid} />
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {stoppedEarly && (
        <p className="mt-2 text-[12.5px] text-ink-soft" data-testid="prediction-stopped-early">
          The AI stopped bidding here; Jeff&apos;s real auction carried on.
        </p>
      )}

      {/* Says in words what the ⊘ marks mean, so the reviewer is not left inferring it from a
          glyph: this hand is outside the engine's rules, not a hand it got wrong. */}
      {divergenceIsDeclined && (
        <p className="mt-2 text-[12.5px] text-ink-soft" data-testid="prediction-declined-note">
          The AI had no rule covering this auction, so it declined to predict rather than
          guessing. Calls marked <span aria-hidden="true">⊘</span> are not wrong answers — there
          is no prediction there to compare.
        </p>
      )}

      <p className="mt-2.5 text-[12px] text-ink-soft">
        Select one of {SEAT_NAMES[jeffSeat]}&apos;s calls to read the AI&apos;s reasoning.
      </p>
    </div>
  );
}

/** One of Jeff's predicted calls: a real button, with its verdict mark beside it. */
function JeffCall({
  entry,
  bid,
  isSelected,
  onSelect,
}: {
  entry: PredictedAuctionEntry;
  bid: PredictedJeffBid | undefined;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const mark = bid ? CORRECTNESS_MARK[bid.correctness] : null;

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={isSelected}
      data-testid={`predicted-bid-${entry.jeffBidOrdinal}`}
      data-correctness={bid?.correctness}
      className={
        // min-h/min-w keep the hit area at or above the 24×24 floor the constitution sets, even
        // for a one-character call like "P".
        'inline-flex min-h-[26px] min-w-[42px] items-center justify-center gap-1 rounded-md px-1.5 ' +
        'font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brass ' +
        (isSelected
          ? 'bg-brass text-on-brass'
          : 'bg-paper text-ink hover:bg-[#efe7cf]')
      }
    >
      <BidToken bid={entry.bid} />
      {mark && (
        <span className={isSelected ? 'text-on-brass' : mark.className}>
          <span aria-hidden="true">{mark.glyph}</span>
          <span className="sr-only"> — {mark.label}</span>
        </span>
      )}
    </button>
  );
}
